import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  ArrowLeftRight, Search, RotateCcw, FileSpreadsheet, FileDown, Loader2, ArrowUp, ArrowDown, ArrowUpDown,
  Truck, PackageOpen, Sigma, CheckCircle2, CircleDashed, Clock, AlertTriangle, HelpCircle, Scale, Layers,
} from "lucide-react";
import {
  REPORT_COLUMNS, SUMMARY_FIELDS, fmtDate, fmtQty, exportReportExcel, exportReportPdf,
} from "@/lib/dcGinReportExport";

// DC vs GIN Report — Reports → DC vs GIN Report. Read-only: every row,
// status and summary figure is computed live by the backend
// (GET /api/reports/dc-vs-gin, see backend/dc_gin_report.py) from the
// existing Delivery Challans and Goods Inward Notes, so a change to either
// shows up on the next Search.

const STATUS_TONE = {
  Matched: "bg-emerald-50 text-emerald-800",
  Partial: "bg-amber-50 text-amber-800",
  Pending: "bg-slate-100 text-slate-700",
  Mismatch: "bg-rose-50 text-rose-800",
  "Unmatched GIN": "bg-violet-50 text-violet-800",
};

const STATUS_OPTIONS = ["Matched", "Partial", "Pending", "Mismatch", "Unmatched GIN"];

const DC_TYPE_LABEL = {
  subcontract: "Job Work (Subcontract)",
  customer: "Customer",
  all: "All DCs",
};

const SORTABLE = new Set([
  "dc_date", "dc_code", "gin_date", "gin_code", "party", "item_code", "item_name",
  "dc_qty", "gin_qty", "difference", "status",
]);

const EMPTY_FILTERS = {
  date_from: "",
  date_to: "",
  dc_no: "",
  gin_no: "",
  party: "",
  item: "",
  status: "all",
  search: "",
  dc_type: "subcontract",
  include_unlinked_gins: false,
};

const PAGE_SIZES = [25, 50, 100, 250];

const SUMMARY_ICONS = {
  total_dcs: { icon: Truck, tone: "text-blue-700 bg-blue-50" },
  total_gins: { icon: PackageOpen, tone: "text-blue-700 bg-blue-50" },
  total_dc_qty: { icon: Sigma, tone: "text-slate-700 bg-slate-100" },
  total_gin_qty: { icon: Sigma, tone: "text-slate-700 bg-slate-100" },
  matched: { icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50", status: "Matched" },
  partial: { icon: CircleDashed, tone: "text-amber-700 bg-amber-50", status: "Partial" },
  pending: { icon: Clock, tone: "text-slate-700 bg-slate-100", status: "Pending" },
  mismatch: { icon: AlertTriangle, tone: "text-red-700 bg-red-50", status: "Mismatch" },
  unmatched_gins: { icon: HelpCircle, tone: "text-violet-700 bg-violet-50", status: "Unmatched GIN" },
  total_difference: { icon: Scale, tone: "text-blue-700 bg-blue-50" },
};

function toParams(f) {
  const p = { dc_type: f.dc_type };
  for (const k of ["date_from", "date_to", "dc_no", "gin_no", "party", "item", "search"]) {
    if (f[k] && String(f[k]).trim()) p[k] = String(f[k]).trim();
  }
  if (f.status && f.status !== "all") p.status = f.status;
  if (f.include_unlinked_gins) p.include_unlinked_gins = true;
  return p;
}

function filterLines(f) {
  return [
    ["Date From", f.date_from ? fmtDate(f.date_from) : "Any"],
    ["Date To", f.date_to ? fmtDate(f.date_to) : "Any"],
    ["DC Number", f.dc_no || "Any"],
    ["GIN Number", f.gin_no || "Any"],
    ["Customer / Party", f.party || "Any"],
    ["Product / Item", f.item || "Any"],
    ["Status", f.status && f.status !== "all" ? f.status : "All"],
    ["Search", f.search || "—"],
    ["DC Type", DC_TYPE_LABEL[f.dc_type] || f.dc_type],
    ["Manual GINs (no DC/PO)", f.include_unlinked_gins ? "Included" : "Excluded"],
  ];
}

function StatusBadge({ status }) {
  return (
    <Badge className={`${STATUS_TONE[status] || "bg-slate-100 text-slate-700"} font-medium border-0 whitespace-nowrap`}>
      {status}
    </Badge>
  );
}

function SummaryCard({ field, value, active, onClick }) {
  const meta = SUMMARY_ICONS[field.key] || {};
  const Icon = meta.icon || Layers;
  const clickable = !!meta.status;
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      title={clickable ? `Show only ${meta.status} rows` : undefined}
      className={`kpi-card !p-4 text-left disabled:cursor-default ${active ? "ring-2 ring-blue-500" : ""}`}
      data-testid={`dcgin-summary-${field.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 leading-tight">{field.label}</div>
          <div className="mt-1.5 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            {fmtQty(value)}
          </div>
        </div>
        <div className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${meta.tone || "bg-slate-100 text-slate-700"}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
}

function SortHeader({ col, sortBy, sortDir, onSort }) {
  const numeric = col.kind === "number";
  const sortable = SORTABLE.has(col.field);
  const active = sortBy === col.field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap border-b border-slate-200 ${numeric ? "text-right" : "text-left"}`}
      data-testid={`dcgin-col-${col.field}`}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort(col.field)}
          className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-blue-700" : ""}`}
        >
          {col.header}
          <Icon className={`w-3 h-3 ${active ? "" : "text-slate-300"}`} />
        </button>
      ) : col.header}
    </th>
  );
}

function DocLink({ children, onClick, tid }) {
  return (
    <button type="button" onClick={onClick} className="mono text-blue-700 hover:underline" data-testid={tid}>
      {children}
    </button>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">{value || value === 0 ? value : "—"}</div>
    </div>
  );
}

function Section({ title, children, tid }) {
  return (
    <section className="border border-slate-200 rounded-lg bg-white" data-testid={tid}>
      <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
        {title}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function DetailView({ target, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    api.get("/reports/dc-vs-gin/detail", { params: target })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (!cancelled) {
          toast.error(formatApiError(err?.response?.data?.detail) || "Could not load details");
          onClose();
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [target, onClose]);

  const dc = data?.dc;
  const party = data?.party || {};
  const s = data?.summary;
  // One status for the whole DC: every line Matched → Matched, any excess →
  // Mismatch, nothing received → Pending, anything in between → Partial.
  const overall = !s || !s.total_rows ? null
    : !s.total_dcs ? "Unmatched GIN"
    : s.mismatch ? "Mismatch"
    : s.matched === s.total_rows ? "Matched"
    : s.pending === s.total_rows ? "Pending"
    : "Partial";

  return (
    <Sheet open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="dcgin-detail">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2" style={{ fontFamily: "Manrope" }}>
            {dc ? `DC ${dc.code}` : data?.gins?.[0] ? `GIN ${data.gins[0].code}` : "Details"}
            {overall && <StatusBadge status={overall} />}
          </SheetTitle>
        </SheetHeader>
        {loading || !data ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {s && (
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">DC Qty</div>
                  <div className="text-lg font-bold text-slate-900">{fmtQty(s.total_dc_qty)}</div>
                </div>
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">GIN Qty</div>
                  <div className="text-lg font-bold text-slate-900">{fmtQty(s.total_gin_qty)}</div>
                </div>
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Difference</div>
                  <div className={`text-lg font-bold ${s.total_difference ? "text-rose-700" : "text-emerald-700"}`}>{fmtQty(s.total_difference)}</div>
                </div>
              </div>
            )}

            <Section title={party.type || "Customer / Party"} tid="dcgin-detail-party">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" value={party.name || dc?.party} />
                <Field label="Code" value={party.code} />
                <Field label="GSTIN" value={party.gstin} />
                <Field label="Phone" value={party.phone} />
                <Field label="Contact" value={party.contact} />
                <Field label="Email" value={party.email} />
                <div className="col-span-2"><Field label="Address" value={party.address} /></div>
              </div>
            </Section>

            {dc ? (
              <Section title="Delivery Challan" tid="dcgin-detail-dc">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <Field label="DC Number" value={<span className="mono">{dc.code}</span>} />
                  <Field label="DC Date" value={fmtDate(dc.dc_date)} />
                  <Field label="Type" value={dc.kind === "subcontract" ? "Job Work (Subcontract)" : "Customer"} />
                  <Field label="DC Status" value={dc.status} />
                  <Field label="Created" value={fmtDate(dc.created_at)} />
                  <Field label="Delivered / Received" value={fmtDate(dc.delivered_at)} />
                  {dc.kind === "subcontract" ? (
                    <>
                      <Field label="Job" value={dc.job_code} />
                      <Field label="Stage" value={dc.stage_label || dc.stage} />
                    </>
                  ) : (
                    <>
                      <Field label="Transfer Order" value={dc.so_code} />
                      <Field label="Sales Order" value={dc.customer_so_code} />
                    </>
                  )}
                  <Field label="Vehicle No." value={dc.vehicle_no} />
                  <Field label="Transporter" value={dc.transporter} />
                  <Field label="Created By" value={dc.created_by} />
                  <Field label="Tax Invoice" value={dc.tax_invoice_code} />
                </div>
                {dc.notes && <div className="mb-3"><Field label="Notes" value={dc.notes} /></div>}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Item Code</th>
                        <th className="px-2 py-1.5 text-left">Item Name</th>
                        <th className="px-2 py-1.5 text-left">Returns As</th>
                        <th className="px-2 py-1.5 text-right">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dc.lines || []).map((ln, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-2 py-1.5 mono">{ln.item_code || "—"}</td>
                          <td className="px-2 py-1.5">{ln.item_name || "—"}</td>
                          <td className="px-2 py-1.5">{ln.output_item_code || "—"}</td>
                          <td className="px-2 py-1.5 text-right">{fmtQty(ln.qty)} {ln.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            ) : (
              <div className="rounded-md border border-violet-200 bg-violet-50 text-violet-800 text-sm px-3 py-2">
                No Delivery Challan is linked to this GIN — it is reported as an Unmatched GIN.
              </div>
            )}

            <Section title="Item Comparison" tid="dcgin-detail-items">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Item</th>
                      <th className="px-2 py-1.5 text-right">DC Qty</th>
                      <th className="px-2 py-1.5 text-right">GIN Qty</th>
                      <th className="px-2 py-1.5 text-right">Accepted</th>
                      <th className="px-2 py-1.5 text-right">Rejected</th>
                      <th className="px-2 py-1.5 text-right">Difference</th>
                      <th className="px-2 py-1.5 text-left">Status</th>
                      <th className="px-2 py-1.5 text-left">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.rows || []).map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 align-top">
                        <td className="px-2 py-1.5"><div className="mono">{r.item_code}</div><div className="text-slate-500">{r.item_name}</div></td>
                        <td className="px-2 py-1.5 text-right">{fmtQty(r.dc_qty)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtQty(r.gin_qty)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtQty(r.accepted_qty)}</td>
                        <td className="px-2 py-1.5 text-right">{fmtQty(r.rejected_qty)}</td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${r.difference ? "text-rose-700" : "text-slate-700"}`}>{fmtQty(r.difference)}</td>
                        <td className="px-2 py-1.5"><StatusBadge status={r.status} /></td>
                        <td className="px-2 py-1.5 text-slate-600">{r.remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title={`Goods Inward Notes (${(data.gins || []).length})`} tid="dcgin-detail-gins">
              {(data.gins || []).length === 0 ? (
                <div className="text-sm text-slate-400">No GIN has been received against this DC yet.</div>
              ) : (
                <div className="space-y-4">
                  {data.gins.map((g) => (
                    <div key={g.id} className="rounded-md border border-slate-200 p-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                        <Field label="GIN Number" value={<span className={`mono ${target?.gin_id === g.id ? "text-blue-700 font-semibold" : ""}`}>{g.code}</span>} />
                        <Field label="GIN Date" value={fmtDate(g.created_at)} />
                        <Field label="GIN Status" value={(g.status || "").replace(/_/g, " ")} />
                        <Field label="Supplier / Party" value={g.supplier} />
                        <Field label="Invoice No." value={g.invoice_no} />
                        <Field label="Vehicle No." value={g.vehicle_no} />
                        <Field label="Inspected By" value={g.inspected_by} />
                        <Field label="Inspected At" value={fmtDate(g.inspected_at)} />
                        <Field label="GRN" value={g.grn_code} />
                      </div>
                      {g.notes && <div className="mb-3"><Field label="Notes" value={g.notes} /></div>}
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                            <tr>
                              <th className="px-2 py-1.5 text-left">Item Code</th>
                              <th className="px-2 py-1.5 text-left">Item Name</th>
                              <th className="px-2 py-1.5 text-right">Received</th>
                              <th className="px-2 py-1.5 text-right">Accepted</th>
                              <th className="px-2 py-1.5 text-right">Rejected</th>
                              <th className="px-2 py-1.5 text-left">Store</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(g.lines || []).map((ln, i) => (
                              <tr key={i} className="border-b border-slate-100">
                                <td className="px-2 py-1.5 mono">{ln.item_code || "—"}</td>
                                <td className="px-2 py-1.5">{ln.item_name || "—"}</td>
                                <td className="px-2 py-1.5 text-right">{fmtQty(ln.received_qty)} {ln.uom}</td>
                                <td className="px-2 py-1.5 text-right">{fmtQty(ln.accepted_qty)}</td>
                                <td className="px-2 py-1.5 text-right">{fmtQty(ln.rejected_qty)}</td>
                                <td className="px-2 py-1.5">{ln.target_store || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Transaction History" tid="dcgin-detail-history">
              {(data.history || []).length === 0 ? (
                <div className="text-sm text-slate-400">No related transactions recorded.</div>
              ) : (
                <ol className="relative border-l border-slate-200 ml-2 space-y-3">
                  {data.history.map((h, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-blue-600" />
                      <div className="text-sm text-slate-900">
                        <span className="font-medium">{h.event}</span>
                        {h.ref && <span className="mono text-slate-600"> · {h.ref}</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {h.at ? new Date(h.at).toLocaleString("en-IN") : "Date not recorded"}
                        {h.by ? ` · ${h.by}` : ""}
                        {h.detail ? ` · ${h.detail}` : ""}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function DcVsGinReport() {
  const [form, setForm] = useState(EMPTY_FILTERS);       // what's typed in the filter bar
  const [applied, setApplied] = useState(EMPTY_FILTERS); // what the report currently reflects
  const [tab, setTab] = useState("dc");                  // "dc" | "unmatched_gin"
  const [sortBy, setSortBy] = useState("dc_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get("/reports/dc-vs-gin", {
      params: { ...toParams(applied), row_type: tab, sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize },
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err?.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [applied, tab, sortBy, sortDir, page, pageSize]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const runSearch = (e) => {
    e?.preventDefault();
    if (form.date_from && form.date_to && form.date_from > form.date_to) {
      toast.error("Date From must be on or before Date To");
      return;
    }
    setPage(1);
    setApplied({ ...form });
  };

  const reset = () => {
    setForm(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
    setSortBy("dc_date");
    setSortDir("desc");
  };

  const onSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir(field.endsWith("_date") ? "desc" : "asc"); }
    setPage(1);
  };

  const filterByStatus = (status) => {
    const next = applied.status === status ? "all" : status;
    const f = { ...applied, status: next };
    setForm(f);
    setApplied(f);
    setPage(1);
    if (next === "Unmatched GIN") setTab("unmatched_gin");
    else if (next !== "all") setTab("dc");
  };

  const closeDetail = useCallback(() => setDetail(null), []);

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const common = { ...toParams(applied), sort_by: sortBy, sort_dir: sortDir, page_size: 0 };
      const [dcRes, unRes] = await Promise.all([
        api.get("/reports/dc-vs-gin", { params: { ...common, row_type: "dc" } }),
        api.get("/reports/dc-vs-gin", { params: { ...common, row_type: "unmatched_gin" } }),
      ]);
      const payload = {
        dcRows: dcRes.data.rows || [],
        unmatchedRows: unRes.data.rows || [],
        summary: dcRes.data.summary,
        filterLines: filterLines(applied),
      };
      if (!payload.dcRows.length && !payload.unmatchedRows.length) {
        toast.error("No rows to export — adjust or clear the filters first.");
        return;
      }
      if (kind === "excel") exportReportExcel(payload); else exportReportPdf(payload);
      toast.success(`Exported ${payload.dcRows.length + payload.unmatchedRows.length} rows to ${kind === "excel" ? "Excel" : "PDF"}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting("");
    }
  };

  const rows = useMemo(() => data?.rows || [], [data]);
  const total = data?.total_rows || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const counts = data?.row_counts || { dc: 0, unmatched_gin: 0 };
  const parties = data?.parties || [];
  const pageTotals = useMemo(() => ({
    dc_qty: rows.reduce((a, r) => a + Number(r.dc_qty || 0), 0),
    gin_qty: rows.reduce((a, r) => a + Number(r.gin_qty || 0), 0),
    difference: rows.reduce((a, r) => a + Number(r.difference || 0), 0),
  }), [rows]);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const renderCell = (row, col) => {
    const v = row[col.field];
    switch (col.field) {
      case "dc_code":
        return row.dc_id && row.row_type === "dc"
          ? <DocLink tid={`dcgin-dc-link-${row.id}`} onClick={() => setDetail({ dc_id: row.dc_id })}>{v}</DocLink>
          : (v ? <span className="mono text-slate-500">{v}</span> : <span className="text-slate-300">—</span>);
      case "gin_code":
        if (!row.gin_ids?.length) return <span className="text-slate-300">—</span>;
        return (
          <span className="flex flex-wrap gap-x-1.5">
            {row.gin_ids.map((gid, i) => (
              <DocLink key={gid} tid={`dcgin-gin-link-${row.id}-${i}`} onClick={() => setDetail({ gin_id: gid })}>
                {row.gin_codes[i]}{i < row.gin_ids.length - 1 ? "," : ""}
              </DocLink>
            ))}
          </span>
        );
      case "gin_date":
        return (row.gin_dates || []).length
          ? row.gin_dates.map(fmtDate).join(", ")
          : <span className="text-slate-300">—</span>;
      case "dc_date":
        return v ? fmtDate(v) : <span className="text-slate-300">—</span>;
      case "status":
        return <StatusBadge status={v} />;
      case "difference":
        return <span className={`font-semibold ${Number(v) > 0 ? "text-amber-700" : Number(v) < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtQty(v)}</span>;
      case "remarks":
        return <span className="block max-w-xs whitespace-normal text-slate-600">{v || "—"}</span>;
      case "item_name":
        return <span className="block max-w-[16rem] whitespace-normal">{v || "—"}</span>;
      default:
        if (col.kind === "number") return fmtQty(v);
        return v || <span className="text-slate-300">—</span>;
    }
  };

  return (
    <div className="space-y-5" data-testid="dc-vs-gin-report-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Reports</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <ArrowLeftRight className="w-7 h-7 text-blue-600" /> DC vs GIN Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Delivery Challan lines compared with the Goods Inward Notes received against them — status is
            computed live from the current DC and GIN records.
          </p>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={runSearch} className="bg-white border border-slate-200 rounded-lg p-4" data-testid="dcgin-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <div>
            <Label htmlFor="dcgin-date-from" className="text-xs text-slate-600">Date From</Label>
            <Input id="dcgin-date-from" type="date" value={form.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-9 mt-1" data-testid="dcgin-date-from" />
          </div>
          <div>
            <Label htmlFor="dcgin-date-to" className="text-xs text-slate-600">Date To</Label>
            <Input id="dcgin-date-to" type="date" value={form.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-9 mt-1" data-testid="dcgin-date-to" />
          </div>
          <div>
            <Label htmlFor="dcgin-dc-no" className="text-xs text-slate-600">DC Number</Label>
            <Input id="dcgin-dc-no" value={form.dc_no} onChange={(e) => set("dc_no")(e.target.value)} placeholder="e.g. DC-6001" className="h-9 mt-1" data-testid="dcgin-dc-no" />
          </div>
          <div>
            <Label htmlFor="dcgin-gin-no" className="text-xs text-slate-600">GIN Number</Label>
            <Input id="dcgin-gin-no" value={form.gin_no} onChange={(e) => set("gin_no")(e.target.value)} placeholder="e.g. GIN-1001" className="h-9 mt-1" data-testid="dcgin-gin-no" />
          </div>
          <div>
            <Label htmlFor="dcgin-party" className="text-xs text-slate-600">Customer / Party</Label>
            <Input id="dcgin-party" list="dcgin-party-options" value={form.party} onChange={(e) => set("party")(e.target.value)} placeholder="Customer or vendor" className="h-9 mt-1" data-testid="dcgin-party" />
            <datalist id="dcgin-party-options">
              {parties.map((p) => <option key={p} value={p} />)}
            </datalist>
          </div>
          <div>
            <Label htmlFor="dcgin-item" className="text-xs text-slate-600">Product / Item</Label>
            <Input id="dcgin-item" value={form.item} onChange={(e) => set("item")(e.target.value)} placeholder="Item code or name" className="h-9 mt-1" data-testid="dcgin-item" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger className="h-9 mt-1" data-testid="dcgin-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">DC Type</Label>
            <Select value={form.dc_type} onValueChange={set("dc_type")}>
              <SelectTrigger className="h-9 mt-1" data-testid="dcgin-dc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(DC_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
            <Label htmlFor="dcgin-search" className="text-xs text-slate-600">Search</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input id="dcgin-search" value={form.search} onChange={(e) => set("search")(e.target.value)} placeholder="Any DC / GIN no., party, item, status or remark" className="h-9 pl-8" data-testid="dcgin-search" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" title="Also list GINs raised by hand with neither a DC nor a Purchase Order reference as Unmatched GINs">
            <Checkbox checked={form.include_unlinked_gins} onCheckedChange={(v) => set("include_unlinked_gins")(!!v)} data-testid="dcgin-include-unlinked" />
            Include manual GINs with no DC / PO reference
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="dcgin-search-btn">
              <Search className="w-3.5 h-3.5 mr-1.5" /> Search
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={reset} data-testid="dcgin-reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => doExport("excel")} disabled={!!exporting} data-testid="dcgin-export-excel">
              {exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />}
              Export to Excel
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="dcgin-export-pdf">
              {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
              Export to PDF
            </Button>
          </div>
        </div>
      </form>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="dcgin-summary">
        {SUMMARY_FIELDS.map((f) => (
          <SummaryCard
            key={f.key}
            field={f}
            value={data?.summary?.[f.key] ?? 0}
            active={!!SUMMARY_ICONS[f.key]?.status && applied.status === SUMMARY_ICONS[f.key].status}
            onClick={() => filterByStatus(SUMMARY_ICONS[f.key].status)}
          />
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" role="tablist">
        {[["dc", "DC vs GIN", counts.dc], ["unmatched_gin", "Unmatched GINs", counts.unmatched_gin]].map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => { setTab(k); setPage(1); }}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            data-testid={`dcgin-tab-${k}`}
          >
            {label} <span className="ml-1 text-xs rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5">{n}</span>
          </button>
        ))}
      </div>

      <div className="relative border border-slate-200 rounded-lg bg-white overflow-x-auto scroll-thin" data-testid="dcgin-table-wrap">
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        <table className="min-w-full text-xs" data-testid="dcgin-table">
          <thead className="bg-slate-50">
            <tr>
              {REPORT_COLUMNS.map((col) => (
                <SortHeader key={col.field} col={col} sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/70 align-top" data-testid={`dcgin-row-${row.id}`}>
                {REPORT_COLUMNS.map((col) => (
                  <td key={col.field} className={`px-3 py-2 whitespace-nowrap text-slate-700 ${col.kind === "number" ? "text-right tabular-nums" : ""} ${col.field === "item_code" ? "mono" : ""}`}>
                    {renderCell(row, col)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={REPORT_COLUMNS.length} className="text-center py-16 text-slate-400">
                  {tab === "dc" ? "No DC records match the current filters." : "No unmatched GINs for the current filters."}
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50">
              <tr className="border-t-2 border-slate-300 font-bold text-slate-900">
                <td className="px-3 py-2" colSpan={7}>Page total</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtQty(pageTotals.dc_qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtQty(pageTotals.gin_qty)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtQty(pageTotals.difference)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
        <div data-testid="dcgin-row-count">
          Showing {rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} row{total === 1 ? "" : "s"}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-8 text-xs" data-testid="dcgin-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage(1)} data-testid="dcgin-page-first">«</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="dcgin-page-prev">‹</Button>
            <span className="text-xs px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="dcgin-page-next">›</Button>
            <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="dcgin-page-last">»</Button>
          </div>
        </div>
      </div>

      <DetailView target={detail} onClose={closeDetail} />
    </div>
  );
}
